import { maxBy } from "lodash-es";

import { serverEnsureDraftPublication } from "api/api-server-publishing";
import { PublicationStatus, PublicationType, SectionKind } from "components/server-types";
import { slices, store } from "store/index";
import { publicationSelectors, sectionSelectors } from "store/selectors";
import { isCCMEnabled } from "utils/waffle-flags";

// TODO: replace with actual selector once publishing UI branch is merged
function getLatestPublicationForSection(
  state: ReturnType<typeof store.getState>,
  sectionId: string,
): PublicationType | undefined {
  const pubs = publicationSelectors.selectAll(state).filter((p) => p.section_id === sectionId);
  return maxBy(pubs, "version");
}

/**
 * When the ShareDB section is a template and the latest publication is still `completed`,
 * optimistically mark it draft (so concurrent ops skip duplicate work) and request
 * `ensure_draft` on the server.
 */
export async function ensurePublicationDraft(sectionId: string | undefined): Promise<void> {
  if (!sectionId) {
    return;
  }

  if (!isCCMEnabled()) {
    return;
  }

  const state = store.getState();
  const section = sectionSelectors.selectById(state, sectionId);
  const latestPub = getLatestPublicationForSection(state, sectionId);

  if (section?.kind !== SectionKind.TEMPLATE || latestPub?.status !== PublicationStatus.COMPLETED) {
    return;
  }

  const previousPub = latestPub;
  store.dispatch(
    slices.publications.actions.updateOne({
      ...latestPub,
      status: PublicationStatus.DRAFT,
    }),
  );

  try {
    const publication = await serverEnsureDraftPublication(sectionId);
    if (publication === null) {
      store.dispatch(slices.publications.actions.updateOne(previousPub));
    }
  } catch {
    store.dispatch(slices.publications.actions.updateOne(previousPub));
  }
}
